import React, { useState, useRef, ChangeEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Upload, FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { processUploadedFile, FileProcessingResponse } from '@/services/spreadsheetService';

interface FileUploadMappingProps {
  onFileProcessed: (data: FileProcessingResponse & { file: File }) => void;
}

const FileUploadMapping: React.FC<FileUploadMappingProps> = ({ onFileProcessed }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    
    if (!files || files.length === 0) {
      setSelectedFile(null);
      return;
    }
    
    const file = files[0];
    console.log(`File selected: ${file.name}, type: ${file.type}, size: ${file.size} bytes`);
    
    // Check if it's a supported file type
    const fileName = file.name.toLowerCase();
    if (!fileName.endsWith('.csv') && !fileName.endsWith('.xlsx') && !fileName.endsWith('.xls')) {
      toast.error('Please select a CSV or Excel file');
      return;
    }
    
    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file to upload');
      return;
    }
    
    setIsProcessing(true);
    try {
      toast.info('Processing is in progress...');
      const result = await processUploadedFile(selectedFile);
      onFileProcessed({ ...result, file: selectedFile });
      toast.success('File processed successfully');
    } catch (error) {
      console.error('Processing failed:', error);
      toast.error(`Failed to process file: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const triggerFileSelect = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div className="space-y-4">
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 cursor-pointer hover:border-gray-400 transition-colors" onClick={triggerFileSelect}>
        <input
          type="file"
          accept=".csv,.xlsx,.xls"
          onChange={handleFileChange}
          ref={fileInputRef}
          className="hidden"
          disabled={isProcessing}
        />
        <div className="flex flex-col items-center justify-center gap-3">
          <div className="rounded-full bg-blue-100 p-3">
            <FileText size={24} className="text-primary" />
          </div>
          <div className="space-y-1 text-center">
            <p className="text-sm font-medium text-gray-900">
              {selectedFile 
                ? `Selected: ${selectedFile.name}` 
                : "Click to select a file"}
            </p>
            <p className="text-xs text-gray-500">
              Excel (.xlsx, .xls) or CSV files accepted
            </p>
          </div>
        </div>
      </div>

      {selectedFile && (
        <Button
          onClick={handleUpload}
          disabled={isProcessing}
          className="w-full flex items-center gap-2"
        >
          {isProcessing ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <Upload size={16} />
              Process File
            </>
          )}
        </Button>
      )}
    </div>
  );
};

export default FileUploadMapping;